import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { PAGE_KEY } from './urlPage';
import { useSessionStorage } from 'usehooks-ts';

export const ATTENDEES_KEY = 'attendees';

export interface AttendeeSessionStorage {
  aliases: string[];
  addAttendees: (attendees: string[]) => void;
  removeAttendees: (attendees: string[]) => void;
  resetAttendees: () => void;
}

export function useAttendeeSessionStorage(): AttendeeSessionStorage {
  const [, setSearchParams] = useSearchParams();
  const [aliases, setAliases] = useSessionStorage<string[]>(
    ATTENDEES_KEY,
    sessionStorage.getItem(ATTENDEES_KEY)?.split(',') || []
  );

  const updateAttendees = (attendees: string[]) => {
    setAliases(attendees);
  };

  const addAttendees = (newAttendees: string[]) => {
    const attendees = new Set([...aliases, ...newAttendees]);
    updateAttendees(Array.from(attendees));
  };

  const removeAttendees = (attendees: string[]) => {
    updateAttendees(aliases.filter((alias: string) => !attendees.includes(alias)));
  };

  const resetAttendees = () => {
    setSearchParams((params) => {
      setAliases([]);
      params.delete(PAGE_KEY);
      return params;
    });
  };

  return {
    aliases,
    addAttendees,
    removeAttendees,
    resetAttendees,
  };
}

/**
 * Non-reactive attendee session storage operations.
 *
 * Use this hook when:
 * - Operating from modals that would cause parent component refresh
 * - Operations where you don't want immediate UI updates
 * - When you need to modify session storage without triggering React re-renders
 *
 * Use the regular useAttendeeSessionStorage() for normal component operations
 * that need reactive updates.
 */
export function useAttendeeSessionStorageNonReactive() {
  const getAttendees = useCallback((): string[] => {
    const stored = sessionStorage.getItem(ATTENDEES_KEY);
    return stored ? JSON.parse(stored) : [];
  }, []);

  const addAttendees = useCallback((newAttendees: string[]) => {
    const stored = sessionStorage.getItem(ATTENDEES_KEY);
    const current = stored ? JSON.parse(stored) : [];
    const combined = new Set([...current, ...newAttendees]);
    sessionStorage.setItem(ATTENDEES_KEY, JSON.stringify(Array.from(combined)));
  }, []);

  const removeAttendees = useCallback((attendeesToRemove: string[]) => {
    const stored = sessionStorage.getItem(ATTENDEES_KEY);
    const current = stored ? JSON.parse(stored) : [];
    const filtered = current.filter((alias: string) => !attendeesToRemove.includes(alias));
    sessionStorage.setItem(ATTENDEES_KEY, JSON.stringify(filtered));
  }, []);

  const clearAttendees = useCallback(() => {
    sessionStorage.setItem(ATTENDEES_KEY, JSON.stringify([]));
  }, []);

  return {
    getAttendees,
    addAttendees,
    removeAttendees,
    clearAttendees,
  };
}
